import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from '../utils/axios';

const BlogManagement = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // --- Dialog States ---
  const [openFormModal, setOpenFormModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedBlogId, setSelectedBlogId] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
  });

  // View Modal State
  const [viewingBlog, setViewingBlog] = useState(null);

  // --- Fetch All Blogs ---
  const fetchBlogs = async () => {
    try {
      const response = await axios.get('/blogs');
      setBlogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // --- Form Input Handlers ---
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // --- Open Add Modal ---
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setSelectedBlogId(null);
    setFormData({ title: '', content: '', author: '' });
    setOpenFormModal(true);
  };

  // --- Open Edit Modal ---
  const handleOpenEdit = (blog) => {
    setIsEditMode(true);
    setSelectedBlogId(blog._id);
    setFormData({
      title: blog.title || '',
      content: blog.content || '',
      author: blog.author || '',
    });
    setOpenFormModal(true);
  };

  // --- Open View Modal ---
  const handleOpenView = (blog) => {
    setViewingBlog(blog);
    setOpenViewModal(true);
  };

  // --- Submit Add / Edit Form ---
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        // Backend PUT endpoint: /blogs/:id
        await axios.put(`/blogs/${selectedBlogId}`, formData);
        toast.success('Blog updated successfully');
      } else {
        // Backend POST endpoint: /blogs
        await axios.post('/blogs', formData);
        toast.success('Blog created successfully');
      }
      setOpenFormModal(false);
      fetchBlogs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Action failed');
    }
  };

  // --- Delete Blog ---
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        await axios.delete(`/blogs/${id}`);
        toast.success('Blog deleted successfully');
        fetchBlogs();
      } catch (error) {
        toast.error('Failed to delete blog');
      }
    }
  };

  // --- Toggle Status ---
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      // Endpoint updated to matches your backend PATCH route: /blogs/:id/toggle
      await axios.patch(`/blogs/${id}/toggle`, { isActive: !currentStatus });
      toast.success(`Blog ${currentStatus ? 'disabled' : 'enabled'} successfully`);
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to update blog status');
    }
  };

  // --- Pagination Handlers ---
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ bgcolor: 'transparent', minHeight: '100vh', p: 4 }}>
      <Box sx={{ mt: -3 }}>
        <Paper elevation={0} sx={{ p: 4, bgcolor: 'transparent' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h4" component="h1">
              Blog Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
            >
              Add New Blog
            </Button>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blogs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((blog) => (
                    <TableRow key={blog._id}>
                      <TableCell>{blog.title}</TableCell>
                      <TableCell>{blog.author}</TableCell>
                      <TableCell>
                        {new Date(blog.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color={blog.isActive ? 'success' : 'error'}
                          size="small"
                          onClick={() => handleToggleStatus(blog._id, blog.isActive)}
                        >
                          {blog.isActive ? 'Active' : 'Inactive'}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Tooltip title="View">
                            <IconButton onClick={() => handleOpenView(blog)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleOpenEdit(blog)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleDelete(blog._id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={blogs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* --- ADD / EDIT DIALOG MODAL --- */}
      <Dialog
        open={openFormModal}
        onClose={() => setOpenFormModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmitForm}>
          <DialogTitle>{isEditMode ? 'Edit Blog' : 'Create New Blog'}</DialogTitle>
          <DialogContent dividers>
            <TextField
              margin="dense"
              name="title"
              label="Blog Title"
              type="text"
              fullWidth
              required
              value={formData.title}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="author"
              label="Author Name"
              type="text"
              fullWidth
              required
              value={formData.author}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="content"
              label="Content"
              multiline
              rows={5}
              fullWidth
              required
              value={formData.content}
              onChange={handleInputChange}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenFormModal(false)} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* --- VIEW BLOG DIALOG MODAL --- */}
      <Dialog
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{viewingBlog?.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Author: {viewingBlog?.author} | Created:{' '}
            {viewingBlog?.createdAt &&
              new Date(viewingBlog.createdAt).toLocaleDateString()}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, whitespace: 'pre-line' }}>
            {viewingBlog?.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewModal(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BlogManagement;